(() => {
  if (window.NightglowProductInfo) return;
  window.NightglowProductInfo = true;

  const syncSelectedOption = (input) => {
    if (!input || input.type !== 'radio') return;

    const fieldset = input.closest('[data-nightglow-option-fieldset]');
    if (!fieldset) return;

    const output = fieldset.querySelector('[data-nightglow-selected-value]');
    if (!output) return;

    output.textContent = input.value;
  };

  const updateAddonOverflow = (container) => {
    const list = container.querySelector('[data-nightglow-addons-list]');
    if (!list) return;

    const tolerance = 3;
    const isOverflowing = list.scrollWidth > list.clientWidth + tolerance;
    const isAtEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - tolerance;

    container.classList.toggle('is-overflowing', isOverflowing);
    container.classList.toggle('is-at-end', !isOverflowing || isAtEnd);
  };

  const prefersReducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  const finishAccordionAnimation = (details, animation, open) => {
    if (details._nightglowAccordionAnimation !== animation) return;

    if (!open) details.open = false;

    details.style.height = '';
    details.style.overflow = '';
    details.dataset.nightglowAnimating = 'false';
    details._nightglowAccordionAnimation = null;
  };

  const animateAccordion = (details, open) => {
    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector('[data-nightglow-accordion-content]');
    if (!summary || !content) return;

    details._nightglowAccordionAnimation?.cancel();

    if (prefersReducedMotion() || typeof details.animate !== 'function') {
      details.open = open;
      return;
    }

    details.dataset.nightglowAnimating = 'true';
    details.style.overflow = 'hidden';

    let startHeight;
    let endHeight;

    if (open) {
      startHeight = `${summary.offsetHeight}px`;
      details.open = true;
      endHeight = `${summary.offsetHeight + content.scrollHeight}px`;
    } else {
      startHeight = `${details.offsetHeight}px`;
      endHeight = `${summary.offsetHeight}px`;
    }

    details.style.height = startHeight;

    const timing = {
      duration: 280,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    };

    const animation = details.animate(
      [{ height: startHeight }, { height: endHeight }],
      timing
    );

    content.animate(
      open ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }],
      timing
    );

    details._nightglowAccordionAnimation = animation;

    animation.onfinish = () => finishAccordionAnimation(details, animation, open);
    animation.oncancel = () => {
      if (details._nightglowAccordionAnimation !== animation) return;
      details.style.height = '';
      details.style.overflow = '';
      details.dataset.nightglowAnimating = 'false';
      details._nightglowAccordionAnimation = null;
    };
  };

  const initAccordion = (details) => {
    if (!details || details.dataset.nightglowAccordionInitialized === 'true') return;

    const summary = details.querySelector(':scope > summary');
    const content = details.querySelector('[data-nightglow-accordion-content]');
    if (!summary || !content) return;

    details.dataset.nightglowAccordionInitialized = 'true';
    details.dataset.nightglowAnimating = 'false';

    summary.addEventListener('click', (event) => {
      if (prefersReducedMotion()) return;

      event.preventDefault();
      if (details.dataset.nightglowAnimating === 'true') return;

      animateAccordion(details, !details.open);
    });
  };

  const initAddonSlider = (container) => {
    if (!container || container.dataset.nightglowInitialized === 'true') return;

    const list = container.querySelector('[data-nightglow-addons-list]');
    if (!list) return;

    container.dataset.nightglowInitialized = 'true';

    const update = () => updateAddonOverflow(container);
    list.addEventListener('scroll', update, { passive: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(list);
      container._nightglowResizeObserver = resizeObserver;
    } else {
      window.addEventListener('resize', update, { passive: true });
    }

    requestAnimationFrame(update);
  };

  const finishVariantAppLoader = (shell, state = 'ready') => {
    if (!shell || shell.dataset.nightglowVariantAppFinished === 'true') return;

    shell.dataset.nightglowVariantAppFinished = 'true';
    shell.classList.remove('is-loading');
    shell.classList.add(state === 'fallback' ? 'is-fallback' : 'is-ready');

    if (shell._nightglowVariantAppObserver) {
      shell._nightglowVariantAppObserver.disconnect();
      shell._nightglowVariantAppObserver = null;
    }

    if (shell._nightglowVariantAppTimer) {
      window.clearTimeout(shell._nightglowVariantAppTimer);
      shell._nightglowVariantAppTimer = null;
    }
  };

  const variantAppIsReady = (shell) => {
    const selector = shell.dataset.nightglowVariantAppSelector || '.globo-swatch-product-detail';
    const productInfo = shell.closest('[id^="ProductInfo-"]') || shell.parentElement;
    if (!productInfo) return false;

    const app = productInfo.querySelector(selector);
    if (!app) return false;

    return Boolean(
      app.querySelector('input, select, button, label, li, [class*="swatch"], [data-value]') ||
      app.children.length > 0 ||
      app.textContent.trim().length > 0
    );
  };

  const initVariantAppLoader = (shell) => {
    if (!shell || shell.dataset.nightglowVariantAppInitialized === 'true') return;

    shell.dataset.nightglowVariantAppInitialized = 'true';

    if (variantAppIsReady(shell)) {
      requestAnimationFrame(() => finishVariantAppLoader(shell));
      return;
    }

    const productInfo = shell.closest('[id^="ProductInfo-"]') || shell.parentElement;
    if (!productInfo) {
      finishVariantAppLoader(shell, 'fallback');
      return;
    }

    const observer = new MutationObserver(() => {
      if (!variantAppIsReady(shell)) return;

      // Give the app one paint to apply its own visibility/classes so the
      // native picker never flashes between the loader and app UI.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => finishVariantAppLoader(shell));
      });
    });

    observer.observe(productInfo, { childList: true, subtree: true, attributes: true });
    shell._nightglowVariantAppObserver = observer;

    // Never leave purchase options inaccessible if the third-party app fails.
    shell._nightglowVariantAppTimer = window.setTimeout(() => {
      finishVariantAppLoader(shell, 'fallback');
    }, 8000);
  };

  const init = (root = document) => {
    root.querySelectorAll?.('[data-nightglow-addons]').forEach(initAddonSlider);
    root.querySelectorAll?.('[data-nightglow-accordion]').forEach(initAccordion);
    root.querySelectorAll?.('[data-nightglow-variant-app-shell]').forEach(initVariantAppLoader);
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('.product--nightglow-info variant-radios input[type="radio"]');
    if (input) syncSelectedOption(input);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('[data-nightglow-addons]')) initAddonSlider(node);
        init(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
